'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import round from 'lodash/round';
import CircularProgress from '@mui/material/CircularProgress';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import GeneralChart from '@/components/seo/GeneralChart';
import OpenJobArea from '@/components/seo/rank/OpenJobArea';
import Button from '@/components/CusButton';
import {
  SalaryRankType,
  OpenJobRankType,
  JobTitleRankType,
} from '@/types/job-ranking';
import setAlertValue from '@/components/Alert';
import {
  JOB_RANKING_PAGE_ROUTER_PATH,
  JOB_TITLE_PAGE_ROUTER_PATH,
  RANK_TOP_POST_PAGE_ROUTER_PATH,
  RANK_TOP_SALARY_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import { HOT_LEVEL, OPEN_JOB_RANK, SALARY_RANK } from '@/constants/job-ranking';
import { getJobTitleRankData } from '@/api/tanzhi';

const DEFAULT_SIZE = 10;
const DEFAULT_PAGE = 2;

type PageDataItemProps = {
  data: OpenJobRankType | SalaryRankType;
  location?: JobTitleRankType['location'];
  index: number;
  isOpenJob?: boolean;
};

type SimplifyListOptionsType = {
  filterId: string;
  title?: string;
  location?: JobTitleRankType['location'];
  subTitle?: string;
  size?: number;
  margin?: string;
  padding?: string;
  border?: string;
  needMore?: boolean;
};

type ListOptionsProps =
  | ({
      isOpenJob: true;
      options: JobTitleRankType['open_job_rank'];
    } & SimplifyListOptionsType)
  | ({
      isOpenJob?: false;
      options: JobTitleRankType['salary_rank'];
    } & SimplifyListOptionsType);

export const PageDataItem = React.memo((props: PageDataItemProps) => {
  const { location, data, isOpenJob, index } = props;
  const router = useRouter();

  const { job_title_name, job_title_id } = data;
  const url = `${location ? `/${location.pinyin}/` : JOB_TITLE_PAGE_ROUTER_PATH}${job_title_id}/`;

  return (
    <li
      className={`flex items-center pl-6 pr-10 py-7 cursor-pointer relative group after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:block after:h-[1px] after:w-[83%] ${isOpenJob ? 'bg-web-green-700/3 after:bg-web-green-200' : 'bg-web-blue-50 after:bg-web-blue-200'} hover:bg-web-blue-500/4`}
      onClick={() => router.push(url)}
    >
      <i
        className={`not-italic w-8 h-8 leading-8 rounded-full text-sm text-center font-semibold mr-4 ${isOpenJob ? 'text-web-green-800 bg-web-green-300' : 'text-web-blue-500 bg-web-blue-300'}`}
      >
        {index + 1}
      </i>
      <Link
        title={job_title_name}
        className="underline group-hover:text-web-blue-500"
        href={url}
      >
        {job_title_name}
      </Link>
      <div className="ml-auto font-semibold flex items-center">
        {isOpenJob ? (
          <>
            <span>{`${(data as OpenJobRankType).current_count}个在招`}</span>
            <OpenJobArea
              areaData={(data as OpenJobRankType).open_job_by_month}
            />
          </>
        ) : (
          <span>{`¥${round((data as SalaryRankType).avg || 0, -2)}`}</span>
        )}
      </div>
    </li>
  );
});

export const ListOptions = React.memo((props: ListOptionsProps) => {
  const {
    title,
    subTitle,
    options,
    location,
    filterId,
    isOpenJob,
    size = DEFAULT_SIZE,
    margin = 'mb-8',
    padding,
    border,
    needMore = true,
  } = props;
  const [pageData, setPageData] = React.useState<
    SalaryRankType[] | OpenJobRankType[] | null
  >(options?.slice(0, size) || null);
  const [showMore, setShowMore] = React.useState(
    options && options.length >= size,
  );
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(DEFAULT_PAGE);

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      const { data } = await getJobTitleRankData(
        filterId === HOT_LEVEL.id ? '' : filterId,
        location?.pinyin,
        page,
      );
      const list = data[isOpenJob ? OPEN_JOB_RANK : SALARY_RANK];
      if (!list || !list.length) {
        setShowMore(false);
        return;
      }
      setPageData((prev) => prev && [...prev, ...list]);
      if (list.length >= DEFAULT_SIZE) {
        setShowMore(true);
        setPage(page + 1);
      } else {
        setShowMore(false);
      }
    } catch (error) {
      setAlertValue({ msg: '数据加载失败' });
    } finally {
      setLoading(false);
    }
  };

  if (!options || !options.length) return null;

  return (
    <GeneralChart
      title={title}
      subTitle={subTitle}
      margin={margin}
      padding={padding}
      border={border}
    >
      <ul className="!ml-0 list-none mt-6">
        {pageData?.map((item, index) => (
          <PageDataItem
            key={item.job_title_id + index}
            location={location}
            data={item}
            isOpenJob={isOpenJob}
            index={index}
          />
        ))}
      </ul>
      {needMore ? (
        <>
          {showMore && (
            <div className="mt-6 flex items-center justify-center">
              <Button
                className="flex items-center font-semibold text-sm !leading-6"
                onClick={handleLoadMore}
                disabled={loading}
              >
                查看更多
                {loading ? (
                  <CircularProgress size={14} className="!ml-1.5 opacity-40" />
                ) : (
                  <KeyboardArrowDownRoundedIcon />
                )}
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          {options.length > size && (
            <div className="mt-6 flex items-center justify-center">
              <Link
                className="flex items-center font-semibold text-sm hover:underline"
                href={`${JOB_RANKING_PAGE_ROUTER_PATH}${location ? `${location.pinyin}_${filterId}` : filterId}${isOpenJob ? RANK_TOP_POST_PAGE_ROUTER_PATH : RANK_TOP_SALARY_PAGE_ROUTER_PATH}`}
              >
                查看更多
                <KeyboardArrowRightRoundedIcon />
              </Link>
            </div>
          )}
        </>
      )}
    </GeneralChart>
  );
});

export default ListOptions;
